// Payment Tracking convergence — HTTP transport for the loan tab's
// Payment System v3 servicing surface (WF1, k=0): schedule-vs-actual
// ledger, manual payment entry, allocation drill-in, servicing-fee config.
// Hooks in @/hooks/portfolioAdmin/useServicing wrap these with TanStack
// Query.

import { api } from "../client";

// --- Vocabulary ---------------------------------------------------------------

export type ServicingPeriodStatus =
  | "upcoming"
  | "on-time"
  | "late"
  | "partial"
  | "missed"
  | "nsf"
  | "prepaid";

export type ServicingPaymentType =
  | "monthly_p_and_i"
  | "balloon_payment"
  | "bmsp_extension_monthly_payment"
  | "full_voluntary_prepayment"
  | "late_fee_event"
  | "nsf_event";

export const SERVICING_PAYMENT_TYPES: ReadonlyArray<{
  value: ServicingPaymentType;
  label: string;
}> = [
  { value: "monthly_p_and_i", label: "Monthly P&I" },
  { value: "balloon_payment", label: "Balloon payment" },
  { value: "bmsp_extension_monthly_payment", label: "BMSP extension payment" },
  { value: "full_voluntary_prepayment", label: "Voluntary prepayment" },
  { value: "late_fee_event", label: "Late fee" },
  { value: "nsf_event", label: "NSF / returned payment" },
];

export type ServicingTraceStatus = "fired" | "zero" | "skipped" | "not_applicable" | "reversed";

// --- Ledger -------------------------------------------------------------------

export interface ServicingLedgerPeriodDto {
  periodIndex: number;
  dueDate: string;
  expectedAmount: number;
  expectedInterest: number;
  expectedPrincipal: number;
  servicingFeeOnTop: number;
  isBalloon: boolean;
  receivedAmount: number;
  receivedDate: string | null;
  status: ServicingPeriodStatus;
  source: "v3" | "legacy" | null;
  paymentId: string | null;
  principalBalanceAfter: number | null;
  // First-Payment Reconciliation (Section C) — this period's reconciliation
  // effect, reported separately from expectedAmount (never a silent bump).
  // Optional on the FE type (the ~13 inline test-literal convention); real API
  // responses always include them (default 0).
  creditApplied?: number;
  priorBalanceCarried?: number;
}

export interface ServicingLedgerSummaryDto {
  daysDelinquent: number;
  openExceptions: number;
  severity: "none" | "warning" | "error";
  receivedYearToDate: number;
  nextDueDate: string | null;
  currentBalance: number;
  scheduledMonthlyPayment: number;
  // First-Payment Reconciliation (Section C) — still-unconsumed boarding
  // reconciliation (never folded into currentBalance). Optional on the FE type;
  // real API responses always include them (default 0).
  creditRemaining?: number;
  priorBalanceRemaining?: number;
  /** BLA Core §2.4 — the Payment Administration Fee on a scheduled monthly
   *  payment, and the combined amount the borrower is actually debited.
   *  Optional on the FE type (the DTO-extension convention); real responses
   *  always include them, and `totalMonthlyDebit` equals
   *  `scheduledMonthlyPayment` on a loan without the fee. */
  paymentAdministrationFee?: number;
  totalMonthlyDebit?: number;
}

export interface ServicingLegacyEntryDto {
  id: string;
  collectionPeriod: string;
  status: string;
  scheduledPaymentAmount: number;
  scheduledPaymentDueDate: string;
  paymentReceivedAmount: number | null;
  paymentReceivedDate: string | null;
  daysLate: number;
  primaryWaterfallRunId: string | null;
}

export interface ServicingLedgerExceptionDto {
  periodIndex: number;
  dueDate: string;
  status: ServicingPeriodStatus;
  expectedAmount: number;
  receivedAmount: number;
  daysPastDue: number;
}

export interface ServicingLedgerDto {
  loanId: string;
  loanNumber: string;
  summary: ServicingLedgerSummaryDto;
  periods: ServicingLedgerPeriodDto[];
  exceptions: ServicingLedgerExceptionDto[];
  legacyEntries: ServicingLegacyEntryDto[];
  // The oldest still-owed period across the FULL remaining term ("next period
  // after the last paid one") — may sit BEYOND the bounded `periods` window when
  // the borrower is paid ahead. The Record Payment wizard defaults its "Applies
  // to period" to this so an ordinary payment never falls back to the
  // calendar-bucket "derive from received date". Null on a retired loan / a loan
  // with no simulatable schedule (older payloads omit it → optional).
  nextPayablePeriod?: ServicingLedgerPeriodDto | null;
  // True when the boarding first payment (period 0) is on file and live. That is
  // exactly the condition under which the server refuses a monthly P&I run
  // against period 0, so a surface offering one is offering a guaranteed no-op.
  // Optional — older payloads omit it, and absent must read as "not blocked".
  boardingFirstPaymentOnFile?: boolean;
  // Borrower-owed fee money that is live now and is in NO period's
  // expectedAmount: the still-open balance of the WF1 obligations behind this
  // loan's approved pipeline fees. A period's expectedAmount is the four
  // schedule terms; the borrower's statement bills expectedAmount PLUS these
  // fees, so a surface prefilling the period's remaining expected asks for less
  // than the borrower was billed. Loan-level (the statement bills every approved
  // fee onto the next bill regardless of the fee's own period), and 0 both on
  // the whole existing book and the moment the fees are collected. Optional —
  // older payloads omit it, and absent must read as "no fees outstanding".
  openBorrowerFeeObligations?: number;
}

// --- Allocation drill-in --------------------------------------------------------

export interface ServicingAllocationLineDto {
  position: number;
  obligationTypeCode: string;
  obligationTypeDisplay: string | null;
  status: ServicingTraceStatus;
  reason: string | null;
  amount: number;
  recipientName: string | null;
  settlementChannel: "servicer" | "phoenix" | null;
  obligationBalanceAfter: number | null;
  disbursementId: string | null;
  // Distribution Gate PR-2 — the recipient's Counterparty id (the FE joins any
  // override on it) + the opening obligation balance. Optional: older payloads
  // omit them.
  recipientEntityId?: string | null;
  obligationBalanceBefore?: number | null;
}

export interface ServicingAllocationDto {
  paymentId: string;
  paymentType: string;
  paymentDate: string;
  amount: number;
  status: string;
  isLegacyMirror: boolean;
  interestPortion: number | null;
  principalPortion: number | null;
  principalBalanceAfter: number | null;
  totalDistributed: number;
  residual: number;
  lines: ServicingAllocationLineDto[];
  // Pay-ahead lineage — set when this payment is a pay-ahead child advanced from
  // a larger recorded payment; the drill-in links back to it. Older payloads omit
  // it (optional).
  paidAheadFromPaymentId?: string | null;
}

// --- Payment entry ---------------------------------------------------------------

export interface RecordServicingPaymentRequest {
  paymentType: ServicingPaymentType;
  amount: number;
  receivedDate: string;
  periodStart?: string | null;
  externalReference?: string | null;
  notes?: string | null;
}

export interface ServicingPaymentRecordedDto {
  paymentId: string;
  wasAlreadyProcessed: boolean;
  totalDistributed: number;
  residual: number;
  /**
   * Distribution Gate (B3 payout gate) — true when the recorded run is held
   * pending_review (its allocation is computed but not yet released for
   * recognition / payout until B3 approves it).
   */
  pendingReview: boolean;
  /**
   * True when `wasAlreadyProcessed` is the period-0 boarding guard rather than
   * ordinary request idempotency: the run targeted the loan's first-payment
   * period, which the boarding first payment already settled 100%-to-Phoenix
   * outside the waterfall. Nothing was recorded and retrying cannot help — so
   * the success panel must say that, not "already recorded, nothing duplicated".
   * Optional: older payloads omit it, and absent reads as ordinary idempotency.
   */
  matchedBoardingFirstPayment?: boolean;
}

// --- Distribution Gate (B3 payout gate): the B3 review queue -----------------------

/** A recorded servicing run awaiting B3 review. */
export interface PendingServicingPaymentDto {
  paymentId: string;
  loanId: string;
  loanNumber: string;
  borrowerName: string | null;
  paymentType: ServicingPaymentType;
  amount: number;
  paymentDate: string;
  recordedAt: string;
  recordedByUserName: string | null;
  totalDistributed: number;
  lineCount: number;
  // Pay-ahead grouping (decision A) — amount/totalDistributed above stay the
  // parent run's OWN figures; these fold in the pay-ahead children so the queue
  // shows the full amount the staff entered as ONE record. For an ordinary
  // payment payAheadCount is 0 and combined* equal the row's own figures. Older
  // payloads omit them (optional).
  payAheadCount?: number;
  combinedAmount?: number;
  combinedTotalDistributed?: number;
}

export interface ServicingReviewResultDto {
  paymentId: string;
  status: string;
}

// --- Distribution Gate PR-2: per-line review, rule checks, corrections -------------

/** A B3 recipient correction on a held run's allocation line (D-13, identity-only). */
export interface ServicingRecipientOverrideDto {
  priorityPosition: number;
  calculatedRecipientEntityId: string | null;
  calculatedRecipientName: string | null;
  finalRecipientEntityId: string;
  finalRecipientName: string | null;
  reason: string;
  updatedAt: string;
  updatedByUserName: string | null;
}

export type ServicingReviewCheckSeverity =
  | "ok"
  | "warning"
  | "error"
  | "not-verified";

/** A gate-time rule-check finding, from persisted data only. */
export interface ServicingReviewCheckDto {
  code: string;
  severity: ServicingReviewCheckSeverity;
  position: number | null;
  message: string;
}

/** One pay-ahead child run inside a review record's internal breakdown. */
export interface ServicingReviewRunDto {
  paymentId: string;
  paymentType: string;
  amount: number;
  totalDistributed: number;
  periodStart: string | null;
  lines: ServicingAllocationLineDto[];
}

/** The per-line B3 review payload for one held run. */
export interface ServicingReviewDto {
  paymentId: string;
  loanId: string;
  paymentType: string;
  status: string;
  amount: number;
  totalDistributed: number;
  waterfallConfigId: string | null;
  waterfallConfigVersion: number | null;
  lines: ServicingAllocationLineDto[];
  overrides: ServicingRecipientOverrideDto[];
  checks: ServicingReviewCheckDto[];
  // Pay-ahead grouping (decision A) — `lines` are the parent run's own; each
  // pay-ahead child is a run in `payAheadRuns` (its own lines + the future
  // period it advances), and combined* sum the parent and every child so the
  // reviewer approves ONE record for the full amount. Empty / zero for an
  // ordinary payment. Older payloads omit them (optional).
  combinedAmount?: number;
  combinedTotalDistributed?: number;
  payAheadRuns?: ServicingReviewRunDto[] | null;
}

export interface SetRecipientOverrideRequest {
  priorityPosition: number;
  finalRecipientEntityId: string;
  reason: string;
}

export interface ClearRecipientOverrideRequest {
  priorityPosition: number;
  reason: string;
}

// --- "Ask AI" payment coach (Record Payment wizard) ----------------------------

export interface RecordPaymentExplainTurn {
  role: "user" | "assistant";
  content: string;
}

/** The wizard's in-progress state — what the coach speaks to. Ledger facts
 *  are re-grounded server-side; this is context, not truth. */
export interface RecordPaymentExplainContext {
  paymentType?: string | null;
  paymentTypeFacts?: string | null;
  amount?: number | null;
  receivedDate?: string | null;
  periodStart?: string | null;
  stepKey?: string | null;
}

export interface RecordPaymentExplainRequest {
  question: string | null;
  history: RecordPaymentExplainTurn[] | null;
  context?: RecordPaymentExplainContext | null;
}

export interface RecordPaymentExplainDto {
  answer: string;
}

// --- Servicing-fee config ----------------------------------------------------------

export type ServicingFeeMode = "deduct_from_payment" | "charge_to_borrower";
export type ServicingFeeAmountType = "flat" | "rate_per_annum";

export interface LoanServicingFeeConfigDto {
  id: string;
  loanId: string;
  mode: ServicingFeeMode;
  amountType: ServicingFeeAmountType;
  amount: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  notes: string | null;
  createdAt: string;
  createdByUserId: string | null;
}

export interface CreateLoanServicingFeeConfigRequest {
  mode: ServicingFeeMode;
  amountType: ServicingFeeAmountType;
  amount: number;
  effectiveFrom: string;
  notes?: string | null;
}

// --- Transport ---------------------------------------------------------------------

export const servicingApi = {
  ledger: (loanId: string) =>
    api.get<ServicingLedgerDto>(`/portfolio-admin/loans/${loanId}/servicing-ledger`),
  recordPayment: (loanId: string, body: RecordServicingPaymentRequest) =>
    api.post<ServicingPaymentRecordedDto>(
      `/portfolio-admin/loans/${loanId}/servicing-payments`,
      body,
    ),
  allocation: (paymentId: string) =>
    api.get<ServicingAllocationDto>(
      `/portfolio-admin/servicing-payments/${paymentId}/allocation`,
    ),
  explainRecordPayment: (loanId: string, body: RecordPaymentExplainRequest) =>
    api.post<RecordPaymentExplainDto>(
      `/portfolio-admin/loans/${loanId}/servicing-payments/explain`,
      body,
    ),
  // Distribution Gate (B3 payout gate) — the B3 review queue.
  pendingReview: () =>
    api.get<PendingServicingPaymentDto[]>(
      `/portfolio-admin/servicing-payments/pending-review`,
    ),
  approveReview: (paymentId: string) =>
    api.post<ServicingReviewResultDto>(
      `/portfolio-admin/servicing-payments/${paymentId}/approve`,
      {},
    ),
  rejectReview: (paymentId: string, reason: string) =>
    api.post<ServicingReviewResultDto>(
      `/portfolio-admin/servicing-payments/${paymentId}/reject`,
      { reason },
    ),
  // Distribution Gate PR-2 — per-line review, rule checks, corrections.
  review: (paymentId: string) =>
    api.get<ServicingReviewDto>(
      `/portfolio-admin/servicing-payments/${paymentId}/review`,
    ),
  setRecipientOverride: (paymentId: string, body: SetRecipientOverrideRequest) =>
    api.post<ServicingReviewDto>(
      `/portfolio-admin/servicing-payments/${paymentId}/recipient-override`,
      body,
    ),
  clearRecipientOverride: (paymentId: string, body: ClearRecipientOverrideRequest) =>
    api.post<ServicingReviewDto>(
      `/portfolio-admin/servicing-payments/${paymentId}/recipient-override/clear`,
      body,
    ),
  recalculateReview: (paymentId: string) =>
    api.post<ServicingReviewResultDto>(
      `/portfolio-admin/servicing-payments/${paymentId}/recalculate`,
      {},
    ),
  feeConfigs: (loanId: string) =>
    api.get<LoanServicingFeeConfigDto[]>(
      `/portfolio-admin/loans/${loanId}/servicing-fee-configs`,
    ),
  createFeeConfig: (loanId: string, body: CreateLoanServicingFeeConfigRequest) =>
    api.post<LoanServicingFeeConfigDto>(
      `/portfolio-admin/loans/${loanId}/servicing-fee-configs`,
      body,
    ),
};
